import { call, put, takeLatest } from 'redux-saga/effects';
import { api } from '../../configurations/api';
import { ASW_S3_LINK, getContestUrl, submissionsUrl } from '../../configurations/api/url';
import { SentryCapture } from '../../analytics/Sentry';
import type { ICurrencyInfo } from '../checkout/types';
import { httpRequest } from '../types';
import type { IContest, IProject, ISaleObject, SubmissionCategory } from '../../types/global/helper';
import { findContestSubmissionCategory } from '../../utils/contestSubmissionMode';
import {
    loadSubmissionPaymentCheckout,
    loadSubmissionPaymentCheckoutFail,
    loadSubmissionPaymentCheckoutSuccess,
} from './actions';

export type SubmissionPaymentCheckoutContext = {
    submissionId: string;
    contest: IContest;
    project: IProject;
    category?: SubmissionCategory;
    chosen_tier?: { label?: string; fee?: number };
    saleObject?: ISaleObject;
    currencyInfo?: ICurrencyInfo;
};

export type LoadSubmissionPaymentCheckoutPayload = {
    submissionId: string;
    fallback?: { project?: IProject; contest?: IContest };
};

const refId = (value: unknown): string | undefined => {
    if (!value) return undefined;
    if (typeof value === 'string') return value === 'event' ? undefined : value;
    if (typeof value === 'object' && value !== null) {
        const id = (value as { _id?: string; id?: string })._id || (value as { id?: string }).id;
        return typeof id === 'string' && id !== 'event' ? id : undefined;
    }
    return undefined;
};

const asPopulatedContest = (value: unknown): IContest | undefined => {
    if (!value || typeof value !== 'object') return undefined;
    const contest = value as IContest;
    return contest._id && Array.isArray(contest.categories) ? contest : undefined;
};

const asPopulatedProject = (value: unknown): IProject | undefined => {
    if (!value || typeof value !== 'object') return undefined;
    const project = value as IProject;
    return project._id ? project : undefined;
};

const absoluteMediaUrl = (url?: string): string | undefined => {
    if (!url) return undefined;
    if (/^https?:\/\//i.test(url)) return url;
    const base = (ASW_S3_LINK || '').replace(/\/?$/, '/');
    return `${base}${url.replace(/^\//, '')}`;
};

const withContestMediaUrls = (contest: IContest): IContest => {
    const feature_image = absoluteMediaUrl(contest.feature_image) || contest.feature_image;
    const social_thumbnail = absoluteMediaUrl(contest.social_thumbnail) || contest.social_thumbnail;
    const contest_media = Array.isArray(contest.contest_media)
        ? contest.contest_media.map(m => absoluteMediaUrl(m) || m)
        : contest.contest_media;
    return { ...contest, feature_image, social_thumbnail, contest_media };
};

const categoryFromPaymentStatus = (statusCategory: any): SubmissionCategory | undefined => {
    if (!statusCategory) return undefined;
    const bare = Number(statusCategory.bare_amount ?? statusCategory.fee);
    const tx =
        statusCategory.transaction_currency ||
        (typeof statusCategory.currency === 'string' && statusCategory.currency.length === 3
            ? statusCategory.currency
            : undefined) ||
        statusCategory.original_currency ||
        'usd';

    return {
        ...statusCategory,
        bare_amount: Number.isFinite(bare) ? bare : statusCategory.bare_amount,
        transaction_currency: String(tx).toLowerCase(),
        original_currency: String(statusCategory.original_currency || tx).toLowerCase(),
        fee: statusCategory.fee,
        formatted_for_display: statusCategory.formatted_for_display,
        defaulted_to_usd: statusCategory.defaulted_to_usd,
        currency: statusCategory.currency,
        rate: statusCategory.rate,
    } as SubmissionCategory;
};

function* loadSubmissionPaymentCheckoutSaga({
    payload,
    resolve,
    reject,
}: {
    payload: LoadSubmissionPaymentCheckoutPayload;
    resolve?: (v: SubmissionPaymentCheckoutContext) => void;
    reject?: (e: any) => void;
}): Generator<any, void, any> {
    try {
        const { submissionId, fallback } = payload;
        const statusRes = yield call(
            api,
            `${submissionsUrl}/${submissionId}/payment-status`,
            httpRequest.GET,
            {},
            0,
            0,
            false,
        );
        const status = statusRes?.data?.data ?? statusRes?.data;
        if (!status?.payment_due) {
            const err = new Error(
                status?.paid ? 'This submission is already paid.' : 'No payment is due for this entry.',
            ) as Error & { code?: string };
            err.code = status?.paid ? 'ALREADY_PAID' : 'NO_PAYMENT_DUE';
            throw err;
        }

        const contestId = refId(status.contest_id);
        if (!contestId) {
            throw new Error('Could not resolve contest for this submission.');
        }

        let contest: IContest | undefined;
        try {
            const contestRes = yield call(api, `${getContestUrl}/${contestId}`, httpRequest.GET, {}, 0, 0, false);
            contest = contestRes?.data?.data ?? contestRes?.data;
        } catch {
            contest = undefined;
        }

        if (!contest?._id) {
            contest = (fallback?.contest?._id ? fallback.contest : undefined) || asPopulatedContest(status.contest_id);
        }
        if (!contest?._id) {
            throw new Error('Could not load contest for payment.');
        }
        contest = withContestMediaUrls(contest);

        const statusCategory = categoryFromPaymentStatus(status.category);
        const contestCategory =
            (statusCategory?._id ? findContestSubmissionCategory(contest, statusCategory._id) : null) ||
            findContestSubmissionCategory(contest, statusCategory?.category_name);

        const resolvedCategory: SubmissionCategory | undefined = statusCategory
            ? ({
                  ...(contestCategory || {}),
                  ...statusCategory,
                  category_name: statusCategory.category_name || contestCategory?.category_name || 'Submission',
              } as SubmissionCategory)
            : contestCategory;

        const chosen_tier =
            status.chosen_tier &&
            (typeof status.chosen_tier.fee === 'number' ||
                typeof status.chosen_tier.bare_amount === 'number' ||
                status.chosen_tier.label)
                ? {
                      label: status.chosen_tier.label,
                      fee:
                          typeof status.chosen_tier.bare_amount === 'number'
                              ? status.chosen_tier.bare_amount
                              : typeof status.chosen_tier.fee === 'number'
                              ? status.chosen_tier.fee
                              : Number(status.chosen_tier.fee) || undefined,
                  }
                : undefined;

        const project =
            (fallback?.project?._id ? fallback.project : undefined) ||
            asPopulatedProject(status.project_id) ||
            ({ _id: refId(status.project_id) } as IProject);

        const txCurrency = String(
            resolvedCategory?.transaction_currency || status.chosen_tier?.transaction_currency || 'usd',
        ).toLowerCase();

        const saleObject: ISaleObject | undefined = resolvedCategory
            ? {
                  category: 'submissions',
                  item: [
                      {
                          category: resolvedCategory,
                          chosen_tier: chosen_tier
                              ? ({
                                    label: chosen_tier.label,
                                    bare_amount: chosen_tier.fee,
                                    fee: chosen_tier.fee,
                                    formatted_for_display: status.chosen_tier?.formatted_for_display,
                                    transaction_currency: txCurrency,
                                } as any)
                              : undefined,
                      },
                  ],
                  transaction_currency: txCurrency,
                  linked_event: contest._id,
                  linked_event_type: 'Contest',
              }
            : undefined;

        const currencyInfo: ICurrencyInfo | undefined = resolvedCategory
            ? {
                  transaction_currency: txCurrency,
                  original_currency: String(
                      resolvedCategory.original_currency || status.category?.original_currency || 'usd',
                  ).toLowerCase(),
                  rate: resolvedCategory.rate,
              }
            : undefined;

        const ctx: SubmissionPaymentCheckoutContext = {
            submissionId,
            contest,
            project,
            category: resolvedCategory,
            chosen_tier,
            saleObject,
            currencyInfo,
        };
        yield put(loadSubmissionPaymentCheckoutSuccess(ctx));
        if (typeof resolve === 'function') resolve(ctx);
    } catch (error: any) {
        SentryCapture(error, 'error');
        yield put(loadSubmissionPaymentCheckoutFail(error));
        if (typeof reject === 'function') reject(error);
    }
}

export default function* submissionPaymentCheckoutSaga() {
    yield takeLatest(loadSubmissionPaymentCheckout as any, loadSubmissionPaymentCheckoutSaga);
}
